import { useEffect, useState } from 'react';
import { Col, message, Modal, Progress, Row, Upload } from 'antd';
import { CustomButton, CustomTitle } from '../../components';
import bulkshipment from '../../assets/images/BulkShipment.svg';
import DataMappingModal from '../Shipments/BulkShipment/DataMappingModal';
import { uploadBulkShipment } from '../../apis/shipment';
import { useSpin } from '../../components/SpinContext';
import { useToken } from '../../common/helper';
import useUnauthorize from '../../hooks/useUnauthorize';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { convertCsvToXlsx } from '../../common/utils/csvToXlsx';
import { LinkInlineIcon } from '../../assets/icons/icons';
import styles from './BulkUploadModal.module.css';

const BulkUploadModal = ({ setModalOpen, modalOpen }) => {
  const [file, setFile] = useState(null);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const { hideSpin } = useSpin();
  const [fileKey, setFileKey] = useState(null);
  const [fieldMappings, setFieldMappings] = useState([]);
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { Dragger } = Upload;
  const handleCancel = () => {
    setModalOpen(false);
    setFile(null);
  };

  useEffect(() => {
    if (modalOpen) {
      setFile(null);
    }
  }, [modalOpen]);

  const uploadFile = (formData) => {
    uploadBulkShipment(token, formData)
      .then((res) => {
        const data = res?.data?.data || null;

        if (data?.is_success) {
          const rawMappings = data?.data?.headers_mapping || [];
          setFieldMappings(
            rawMappings.map((item) =>
              item?.actual_name === 'ignore_sensor_measurement_at_start'
                ? { ...item, actual_name: '', enable: item.enable }
                : item
            )
          );
          setFileKey(data?.data?.key);
          setShowMappingModal(true);
          setModalOpen(false);
          message.success(data?.message || 'Upload successful.');
        } else {
          message.error(
            data?.message?.text || 'Upload failed. Please try again.'
          );
        }
      })
      .catch((error) => {
        unauthorize(error);
        message.error('Upload failed. Please try again.');
      })
      .finally(() => {
        hideSpin();
      });
  };

  const animateProgress = async (newFile) => {
    let fileToUpload = newFile.raw;
    if (fileToUpload.name.toLowerCase().endsWith('.csv')) {
      fileToUpload = await convertCsvToXlsx(fileToUpload);
    }
    const formData = new FormData();
    formData.append('files', fileToUpload);
    uploadFile(formData);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 2;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
      }

      setFile((prev) => ({ ...prev, progress }));
    }, 60);
  };

  const handleFileAdd = (fileObj) => {
    const acceptedExtensions = ['.csv', '.xls', '.xlsx'];
    const isValidType = acceptedExtensions.some((ext) =>
      fileObj?.name?.toLowerCase()?.endsWith(ext)
    );

    const sizeInMB = fileObj.size / (1024 * 1024);
    const isTooSmall = sizeInMB < 0;
    const isTooLarge = sizeInMB > 20;

    if (!isValidType) {
      message.error(`Unsupported file type: ${fileObj.name}`);
      return;
    }

    if (isTooSmall) {
      message.error(`${fileObj.name} is smaller than 1MB.`);
      return;
    }

    if (isTooLarge) {
      message.error(`${fileObj.name} exceeds 20MB.`);
      return;
    }

    const uid = `${fileObj.name}-${Date.now()}`;
    const newFile = { uid, progress: 0, raw: fileObj };
    setFile(newFile);
    animateProgress(newFile);
  };

  return (
    <>
      <Modal
        title=''
        centered
        open={modalOpen}
        onCancel={handleCancel}
        width={590}
        footer={null}
        styles={{ root: { borderRadius: '18px', overflow: 'hidden' } }}
      >
        <CustomTitle
          text='Bulk upload'
          fontSize='20px'
          lineHeight='28px'
          fontWeight='700'
          marginBottom='0px'
        />

        <main className={styles.main}>
          <Dragger
            multiple={false}
            showUploadList={false}
            accept='.csv,.xls,.xlsx'
            beforeUpload={(file) => {
              handleFileAdd(file);
              return false;
            }}
            classNames={{ trigger: styles.dragger }}
          >
            <img
              src={bulkshipment}
              alt='upload illustration'
              className={styles.illustration}
            />
            <CustomTitle
              text='Add shipment data'
              fontSize='20px'
              lineHeight='28px'
              fontWeight='700'
              marginBottom='8px'
            />
            <p className={styles.description}>
              Drop your spreadsheet here to upload
              <br />
              <br />
              For best results, data should include{' '}
              {themeT('tracking', theme.themeType)} ID,{' '}
              {themeT('provider', theme.themeType)} name and Product name.
            </p>
            <CustomButton
              iconPosition='left'
              icon={<LinkInlineIcon />}
              gap={'10px'}
              paddingImage={5}
              text='Browse files'
              bgColor='var(--light-gray)'
              color='var(--text-primary)'
              height='48px'
              width='143px'
              fontSize='14px'
              fontWeight='600'
              boxShadow='none'
              borderRadius='20px'
            />
          </Dragger>

          {file && (
            <Row className={styles.fileRow}>
              <Col span={24}>
                <p className={styles.fileName}>{file.raw.name}</p>

                <Progress
                  percent={file.progress}
                  showInfo={false}
                  strokeColor='#266EF1'
                  trailColor='#D9D9D9'
                />
              </Col>
            </Row>
          )}
        </main>
      </Modal>

      <DataMappingModal
        setFieldMappings={setFieldMappings}
        fieldMappings={fieldMappings}
        setModalOpen={setModalOpen}
        fileKey={fileKey}
        unauthorize={unauthorize}
        showMappingModal={showMappingModal}
        setShowMappingModal={setShowMappingModal}
      />
    </>
  );
};

export default BulkUploadModal;
